import { Injectable } from '@angular/core';
import {
  dispatchGridSearchToggle,
  isGridSearchF12Key,
} from './grid-search-shortcut.util';

/**
 * Single early window capture listener for F12 on billing screens (Fn+F12 on laptops).
 * - Blocks Chrome DevTools when F12 reaches the page
 * - Toggles find only when a grid-search-bar is active and no modal is open
 */
@Injectable({ providedIn: 'root' })
export class GridSearchShortcutService {
  private consumerCount = 0;
  private listenerAttached = false;
  private readonly windowKeydownCapture = (event: KeyboardEvent): void =>
    this.onWindowKeydownCapture(event);

  /** Call once at app bootstrap (before components). */
  initEarlyListener(): void {
    if (this.listenerAttached || typeof window === 'undefined') {
      return;
    }
    window.addEventListener('keydown', this.windowKeydownCapture, true);
    this.listenerAttached = true;
  }

  registerConsumer(): void {
    this.consumerCount++;
  }

  unregisterConsumer(): void {
    this.consumerCount = Math.max(0, this.consumerCount - 1);
  }

  private onWindowKeydownCapture(event: KeyboardEvent): void {
    if (!isGridSearchF12Key(event) || this.consumerCount <= 0) {
      return;
    }

    // Always swallow F12 on billing pages so Chrome DevTools does not open (Fn+F12).
    event.preventDefault();
    event.stopImmediatePropagation();

    if (isGridSearchBlockedByOverlay()) {
      return;
    }

    dispatchGridSearchToggle();
  }
}

/** Shared overlay check for F12 and chord shortcuts. */
export function isGridSearchBlockedByOverlay(): boolean {
  const container = document.querySelector('.cdk-overlay-container');
  if (!container) {
    return false;
  }

  if (
    container.querySelector(
      '.mat-mdc-dialog-container, mat-dialog-container, .mat-mdc-dialog-surface',
    )
  ) {
    return true;
  }

  if (
    container.querySelector(
      'app-customer-selection-dialog, app-item-selection-dialog, app-invoice-search-dialog',
    )
  ) {
    return true;
  }

  const roleDialog = container.querySelector('[role="dialog"]');
  if (roleDialog && !roleDialog.closest('.grid-search-bar')) {
    return true;
  }

  return false;
}
